import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { getBootLocale } from '../i18n';

// Donors are fetched on-demand when the user drills into an ambassador
// from the "הצג הכל" modal. /api/public/... matches the rest of the public
// campaign endpoints used elsewhere on the page.
const API_BASE = '/api';

/** Locale-aware number formatter — keeps Hebrew commas for HE locale,
 * switches to US-style for EN. Used wherever we render amounts. */
const fmtAmount = (n: number): string =>
  Number(n).toLocaleString(getBootLocale() === 'en' ? 'en-US' : 'he-IL');

type AmbassadorDonor = { name: string; amount: number; createdAt: string };

// ─── Shared "ambassadors-public" block — used by the public campaign page
// AND by the campaign-builder preview, so what the campaign owner sees in
// the editor matches what donors see live. The inner block contents only —
// the outer block wrapper (id ref, wrapStyle, baseStyle padding) is left to
// the parent so the dynamic-height measurement and grid-positioning
// machinery there stay in charge.

export type AmbassadorItem = {
  name: string;
  slug: string;
  goal: number;
  raised: number;
  donorsCount: number;
  recentDonors: Array<{ name: string; amount: number; createdAt: string }>;
};

const AmbassadorCard: React.FC<{
  amb: AmbassadorItem; cardBg: string; accent: string; s: any; p: any;
  // onClick = whole-card click (used only for the donor drill-down in the modal).
  // onDonate = the explicit "donate via this ambassador" action — gated behind a
  // confirmation so a donor can't silently change who their donation is credited
  // to just by tapping a card (which caused real mis-attribution).
  // previewDonate renders the button for layout only (editor preview), inert.
  compact?: boolean; onClick?: () => void; onDonate?: () => void; previewDonate?: boolean; active?: boolean;
}> = ({ amb, cardBg, accent, s, p, compact, onClick, onDonate, previewDonate, active }) => {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pct = amb.goal > 0 ? Math.min(100, Math.round((amb.raised / amb.goal) * 100)) : 0;
  // Compact variant lets a card shrink to ~100-120px wide without breaking.
  const pad = compact ? 8 : 14;
  const avSize = compact ? 26 : 36;
  const gap = compact ? 6 : 10;
  const nameSize = compact ? 12 : 14;
  const metaSize = compact ? 10 : 12;
  return (
    <div
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      style={{
        background: cardBg, borderRadius: 12, padding: pad,
        boxShadow: active ? `0 0 0 2px ${accent}, 0 2px 8px rgba(0,0,0,0.12)` : '0 2px 8px rgba(0,0,0,0.08)',
        display: 'flex', flexDirection: 'column', gap: compact ? 4 : 8,
        minWidth: 0,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.2s, transform 0.15s',
      }}
      onMouseEnter={onClick ? (e) => { e.currentTarget.style.transform = 'translateY(-1px)'; } : undefined}
      onMouseLeave={onClick ? (e) => { e.currentTarget.style.transform = 'translateY(0)'; } : undefined}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap, minWidth: 0 }}>
        <div style={{
          width: avSize, height: avSize, borderRadius: '50%', background: `${accent}22`,
          color: accent, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 'bold', fontSize: compact ? 11 : 14, flexShrink: 0,
        }}>
          {amb.name.split(' ').map((w: string) => w[0]).join('').slice(0, 2)}
        </div>
        <div style={{ fontWeight: 700, color: s.textColor || '#111827', fontSize: nameSize, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>{amb.name}</div>
      </div>
      {(p.showRaised !== false || p.showGoal !== false) && (
        <div style={{ fontSize: metaSize, color: '#6b7280', display: 'flex', justifyContent: 'space-between', gap: 4, flexWrap: compact ? 'wrap' : 'nowrap' }}>
          {p.showRaised !== false && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t('public.ambassadors.raised')}: <b style={{ color: '#059669' }}>₪{fmtAmount(amb.raised)}</b></span>}
          {p.showGoal !== false && (
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {t('public.ambassadors.goal')}: {amb.goal > 0 ? `₪${fmtAmount(amb.goal)}` : '—'}
            </span>
          )}
        </div>
      )}
      {p.showProgress !== false && (
        // Always render the progress bar (even when goal=0) so every card
        // shows the same metric layout. When no goal exists we show an empty
        // bar with a "—" instead of "0%" so it's clear that no target is set.
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ flex: 1, height: compact ? 4 : 6, borderRadius: 999, background: '#f3f4f6', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: accent, transition: 'width 0.4s' }} />
          </div>
          <span dir="ltr" style={{ fontSize: compact ? 10 : 11, fontWeight: 800, color: accent, minWidth: 32, textAlign: 'end' }}>
            {amb.goal > 0 ? `${pct}%` : '—'}
          </span>
        </div>
      )}
      {p.showDonorsCount !== false && (
        <div style={{ fontSize: compact ? 10 : 11, color: '#9ca3af' }}>{amb.donorsCount} {t('public.ambassadors.donors')}</div>
      )}
      {!compact && p.showDonorsList !== false && amb.recentDonors.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, borderTop: '1px solid #f3f4f6', paddingTop: 6 }}>
          {amb.recentDonors.slice(0, p.maxDonorsPerCard || 3).map((d, j) => (
            <div key={j} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#6b7280' }}>
              <span>{d.name || t('public.recentDonors.anonymousDonor')}</span>
              <span style={{ color: '#059669', fontWeight: 600 }}>₪{Number(d.amount).toLocaleString('he-IL')}</span>
            </div>
          ))}
        </div>
      )}
      {onDonate && confirmOpen && createPortal(
        (() => {
          const en = getBootLocale() === 'en';
          const initials = amb.name.split(' ').map((w: string) => w[0]).join('').slice(0, 2);
          return (
            <div
              onClick={() => setConfirmOpen(false)}
              style={{
                position: 'fixed', inset: 0, zIndex: 100000,
                background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(3px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
              }}
            >
              <div
                dir={en ? 'ltr' : 'rtl'}
                onClick={e => e.stopPropagation()}
                style={{
                  background: '#fff', borderRadius: 20, padding: '26px 24px 20px', width: '100%', maxWidth: 360,
                  boxShadow: '0 24px 70px rgba(0,0,0,0.3)', textAlign: 'center',
                  animation: 'ambPop .18s ease-out',
                }}
              >
                <style>{'@keyframes ambPop{from{opacity:0;transform:translateY(8px) scale(.97)}to{opacity:1;transform:none}}'}</style>
                <div style={{
                  width: 60, height: 60, borderRadius: '50%', margin: '0 auto 14px',
                  background: `${accent}1a`, color: accent, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontWeight: 800, fontSize: 22,
                }}>{initials}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#111827', marginBottom: 6 }}>
                  {en ? 'Donate via' : 'לתרום דרך'} {amb.name}{en ? '?' : '?'}
                </div>
                <div style={{ fontSize: 13.5, color: '#6b7280', lineHeight: 1.5, marginBottom: 20 }}>
                  {en
                    ? 'Your donation will be credited to this ambassador. 💛'
                    : 'התרומה שלך תיזקף לזכותו/ה. 💛'}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={() => { setConfirmOpen(false); onDonate(); }}
                    style={{
                      flex: 1, padding: '12px', borderRadius: 12, border: 'none', cursor: 'pointer',
                      background: accent, color: '#fff', fontWeight: 800, fontSize: 14.5,
                    }}
                  >{en ? 'Yes, donate' : 'כן, תרום דרכו/ה'}</button>
                  <button
                    onClick={() => setConfirmOpen(false)}
                    style={{
                      padding: '12px 18px', borderRadius: 12, border: '1px solid #e5e7eb', cursor: 'pointer',
                      background: '#fff', color: '#6b7280', fontWeight: 700, fontSize: 14.5,
                    }}
                  >{en ? 'Cancel' : 'ביטול'}</button>
                </div>
              </div>
            </div>
          );
        })(),
        document.body,
      )}
      {(onDonate || previewDonate) && (
        <button
          onClick={(e) => { e.stopPropagation(); if (onDonate) setConfirmOpen(true); }}
          style={{
            // marginTop:auto anchors the button to the BOTTOM of the card. Cards
            // in a row stretch to equal height, so the button lands in the same
            // place on every card regardless of how long its donor list is.
            marginTop: 'auto', padding: compact ? '4px 8px' : '6px 9px',
            borderRadius: 8, border: active ? 'none' : `1px solid ${accent}`, cursor: 'pointer',
            background: active ? accent : `${accent}12`, color: active ? '#fff' : accent,
            fontWeight: 700, fontSize: compact ? 10 : 11, width: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
          }}
        >
          {active
            ? (getBootLocale() === 'en' ? '✓ Selected' : '✓ נבחר')
            : `💛 ${getBootLocale() === 'en' ? 'Donate via' : 'תרום דרך'}`}
        </button>
      )}
    </div>
  );
};

// "Show more" modal — full ambassador list, then drills into a donor table
// per ambassador. Rendered through a React portal to `document.body` so
// position:fixed escapes the canvas's CSS `zoom` container on the public
// page (otherwise the modal would be clipped/scaled inside the canvas and
// appear to overlay the blocks below the ambassadors block).
const AmbassadorAllModal: React.FC<{
  list: AmbassadorItem[]; cardBg: string; accent: string; s: any; p: any;
  title: string; onClose: () => void; campaignSlug?: string;
  // "Donate via" from the search results: select the ambassador (sets ?ref)
  // then close the modal so the donor lands on the form. Lets a donor find an
  // ambassador by name and give through them without hunting the carousel.
  onCardClick?: (slug: string) => void;
}> = ({ list, cardBg, accent, s, p, title, onClose, campaignSlug, onCardClick }) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<{ name: string; slug: string } | null>(null);
  const [donors, setDonors] = useState<AmbassadorDonor[]>([]);
  const [donorsLoading, setDonorsLoading] = useState(false);
  const [donorsError, setDonorsError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // Donor-facing sort inside the "show all" modal. Defaults to amount (the
  // most common "who raised the most" view); donors can re-sort by donor
  // count or name (א-ת) without touching the campaign's authored order.
  const [sortBy, setSortBy] = useState<string>('raised-desc');

  const filteredList = (() => {
    const q = search.trim().toLowerCase();
    const base = q ? list.filter(a => a.name.toLowerCase().includes(q)) : list;
    return sortAmbassadors(base, sortBy);
  })();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (selected) setSelected(null);
      else onClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose, selected]);

  useEffect(() => {
    if (!selected || !campaignSlug) return;
    let cancelled = false;
    setDonorsLoading(true);
    setDonorsError(null);
    setDonors([]);
    axios
      .get(`${API_BASE}/public/campaigns/${campaignSlug}/ambassadors-public/${encodeURIComponent(selected.slug)}/donors`)
      .then(r => { if (!cancelled) setDonors(r.data?.donors || []); })
      .catch(() => { if (!cancelled) setDonorsError(t('public.ambassadors.loadFailed')); })
      .finally(() => { if (!cancelled) setDonorsLoading(false); });
    return () => { cancelled = true; };
  }, [selected, campaignSlug]);

  const modalP = { ...p, showRaised: true, showGoal: true, showProgress: true };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(getBootLocale() === 'en' ? 'en-US' : 'he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return ''; }
  };

  const totalRaised = donors.reduce((sum, d) => sum + (d.amount || 0), 0);

  const modal = (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: 16,
      }}
      onClick={onClose}
    >
      <div
        dir={getBootLocale() === 'en' ? 'ltr' : 'rtl'}
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 20, maxWidth: 720, width: '100%',
          maxHeight: '85vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{
          padding: '18px 22px', borderBottom: '1px solid #f3f4f6',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            {selected && (
              <button
                onClick={() => setSelected(null)}
                aria-label={t('common.back')}
                style={{
                  width: 32, height: 32, borderRadius: '50%', border: 'none',
                  background: '#f3f4f6', color: '#374151', fontSize: 20, lineHeight: 1,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}
              >→</button>
            )}
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selected ? t('public.ambassadors.donorsOf', { name: selected.name }) : `🤝 ${title} (${list.length})`}
            </h3>
          </div>
          <button onClick={onClose} aria-label={t('common.close')} style={{
            width: 32, height: 32, borderRadius: '50%', border: 'none',
            background: '#f3f4f6', color: '#374151', fontSize: 20, lineHeight: 1,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>×</button>
        </div>
        <div style={{
          flex: 1, overflowY: 'auto', padding: 18,
          overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch',
        }}>
          {!selected ? (
            <>
              <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 0 }}>
                  <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder={t('public.ambassadors.searchPlaceholder') + '...'}
                    aria-label={t('public.ambassadors.searchPlaceholder')}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '10px 38px 10px 14px',
                      borderRadius: 10,
                      border: '1px solid #e5e7eb',
                      background: '#f9fafb',
                      fontSize: 14,
                      color: '#111827',
                      outline: 'none',
                      transition: 'border-color 0.15s, background 0.15s',
                    }}
                    onFocus={e => { e.currentTarget.style.borderColor = accent; e.currentTarget.style.background = '#fff'; }}
                    onBlur={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#f9fafb'; }}
                  />
                  <span style={{
                    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                    color: '#9ca3af', fontSize: 16, pointerEvents: 'none',
                  }}>🔍</span>
                  {search && (
                    <button
                      type="button"
                      onClick={() => setSearch('')}
                      aria-label={t('common.search')}
                      style={{
                        position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                        width: 24, height: 24, borderRadius: '50%', border: 'none',
                        background: '#e5e7eb', color: '#374151', fontSize: 14, lineHeight: 1,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >×</button>
                  )}
                </div>
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value)}
                  aria-label={t('public.ambassadors.sortBy')}
                  style={{
                    flex: '0 0 auto',
                    boxSizing: 'border-box',
                    padding: '10px 14px',
                    borderRadius: 10,
                    border: '1px solid #e5e7eb',
                    background: '#f9fafb',
                    fontSize: 14,
                    color: '#111827',
                    outline: 'none',
                    cursor: 'pointer',
                  }}
                  onFocus={e => { e.currentTarget.style.borderColor = accent; e.currentTarget.style.background = '#fff'; }}
                  onBlur={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#f9fafb'; }}
                >
                  <option value="raised-desc">{t('public.ambassadors.sortRaised')}</option>
                  <option value="donors-desc">{t('public.ambassadors.sortDonors')}</option>
                  <option value="name-asc">{t('public.ambassadors.sortName')}</option>
                </select>
              </div>
              {filteredList.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 32, color: '#9ca3af', fontSize: 14 }}>
                  {t('public.ambassadors.noResults')}
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                  gap: 12,
                }}>
                  {filteredList.map((amb, i) => (
                    <AmbassadorCard
                      key={`${amb.slug}-${i}`}
                      amb={amb}
                      cardBg={cardBg}
                      accent={accent}
                      s={s}
                      p={modalP}
                      onClick={() => setSelected({ name: amb.name, slug: amb.slug })}
                      onDonate={onCardClick ? () => { onCardClick(amb.slug); onClose(); } : undefined}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div>
              {donorsLoading && (
                <div style={{ textAlign: 'center', padding: 24, color: '#9ca3af', fontSize: 14 }}>{t('public.ambassadors.loading')}</div>
              )}
              {donorsError && (
                <div style={{ textAlign: 'center', padding: 24, color: '#dc2626', fontSize: 14 }}>{donorsError}</div>
              )}
              {!donorsLoading && !donorsError && donors.length === 0 && (
                <div style={{ textAlign: 'center', padding: 24, color: '#9ca3af', fontSize: 14 }}>
                  {t('public.ambassadors.noDonorsYet', { name: selected.name })}
                </div>
              )}
              {!donorsLoading && !donorsError && donors.length > 0 && (
                <>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '6px 4px 14px', fontSize: 13, color: '#6b7280',
                  }}>
                    <span>{donors.length} {t('public.ambassadors.donors')}</span>
                    <span>{t('public.ambassadors.total')}: <b style={{ color: '#059669' }}>₪{fmtAmount(totalRaised)}</b></span>
                  </div>
                  <div style={{
                    border: '1px solid #f3f4f6', borderRadius: 12, overflow: 'hidden',
                  }}>
                    {donors.map((d, i) => (
                      <div
                        key={i}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr auto auto',
                          gap: 12, alignItems: 'center',
                          padding: '10px 14px',
                          borderTop: i === 0 ? 'none' : '1px solid #f3f4f6',
                          fontSize: 14,
                        }}
                      >
                        <span style={{ color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name || t('public.recentDonors.anonymousDonor')}</span>
                        <span style={{ color: '#9ca3af', fontSize: 12 }}>{formatDate(d.createdAt)}</span>
                        <span style={{ color: '#059669', fontWeight: 700, minWidth: 80, textAlign: 'end' }}>
                          ₪{fmtAmount(d.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Render outside the canvas's zoomed container so position:fixed actually
  // means "viewport" (CSS zoom on an ancestor confines it otherwise).
  return typeof document !== 'undefined' ? createPortal(modal, document.body) : null;
};

const AmbassadorCarousel: React.FC<{
  list: AmbassadorItem[]; perPage: number; cardBg: string; accent: string; s: any; p: any;
  autoRotateMs?: number; compact?: boolean;
  onCardClick?: (slug: string) => void; activeSlug?: string | null;
  showAllButton?: boolean; title?: string; campaignSlug?: string; previewDonate?: boolean;
}> = ({
  list, perPage, cardBg, accent, s, p, autoRotateMs = 5000, compact = false,
  onCardClick, activeSlug, showAllButton = false, title,
  campaignSlug, previewDonate,
}) => {
  const { t } = useTranslation();
  // Default title was 'השגרירים שלנו' — now resolved through i18n so EN donors
  // see 'Our Ambassadors' when no custom title was set.
  const titleEff = title || t('public.ambassadors.title');
  const pages = Math.max(1, Math.ceil(list.length / perPage));
  const [page, setPage] = useState(0);
  const [animate, setAnimate] = useState(true);
  const [paused, setPaused] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const prevPageRef = useRef(0);

  // Transform-based carousel — no scrollLeft, no RTL scroll-math quirks.
  // The inner row is `pages × 100%` wide and is translated by a
  // (100 / pages)% step per page. When wrapping past the last page back
  // to the first (or vice versa) we briefly disable the CSS transition
  // so the row doesn't visibly rewind through every page in between —
  // it just instantly snaps to the new page, giving the "infinite
  // carousel" feel the user expects.
  const goTo = useCallback((target: number) => {
    const clamped = ((target % pages) + pages) % pages;
    const prev = prevPageRef.current;
    const isWrap =
      (prev === pages - 1 && clamped === 0 && target > prev) ||
      (prev === 0 && clamped === pages - 1 && target < prev);
    if (isWrap) {
      setAnimate(false);
      setPage(clamped);
      // Two RAFs: first lets the no-animation transform paint, second
      // re-enables the transition so the NEXT click animates normally.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimate(true));
      });
    } else {
      setAnimate(true);
      setPage(clamped);
    }
    prevPageRef.current = clamped;
  }, [pages]);

  // Auto-rotate; pauses while the user interacts.
  useEffect(() => {
    if (pages <= 1 || paused) return;
    const id = window.setTimeout(() => goTo(page + 1), autoRotateMs);
    return () => window.clearTimeout(id);
  }, [page, pages, paused, autoRotateMs, goTo]);

  if (list.length === 0) {
    return (
      <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: 20 }}>
        {t('public.ambassadors.noAmbassadorsYet')}
      </div>
    );
  }

  const showControls = pages > 1;
  const arrowStyle: React.CSSProperties = {
    position: 'absolute', top: 'calc(50% - 18px)', transform: 'translateY(-50%)',
    width: 36, height: 36, borderRadius: '50%', border: 'none',
    background: '#ffffff', boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
    cursor: 'pointer', fontSize: 22, color: '#374151', lineHeight: 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 3, transition: 'transform 0.15s, background 0.15s',
  };

  // Reserve room on each side for the arrows so they sit OUTSIDE the cards
  // instead of overlapping them. 44px = 36px arrow + 8px breathing space.
  // Skip the reservation entirely if there's only one page (no arrows).
  const sidePad = showControls ? (compact ? 36 : 44) : 0;

  return (
    <div
      style={{ position: 'relative', paddingInline: sidePad }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
    >
      {/* Viewport with overflow:hidden — the inner row is `pages × 100%`
          wide and is translated by transform. dir follows the donor locale:
          HE keeps RTL so page 0 is the rightmost (legacy); EN switches to LTR
          so the carousel scrolls left-to-right naturally. */}
      <div style={{ overflow: 'hidden' }}>
        <div
          dir={getBootLocale() === 'en' ? 'ltr' : 'rtl'}
          style={{
            display: 'flex',
            width: `${pages * 100}%`,
            transform: `translateX(${page * (100 / pages)}%)`,
            transition: animate ? 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
          }}
        >
          {Array.from({ length: pages }, (_, pIdx) => {
            const items = list.slice(pIdx * perPage, (pIdx + 1) * perPage);
            const slots: (AmbassadorItem | null)[] = [...items];
            while (slots.length < perPage) slots.push(null);
            return (
              <div
                key={pIdx}
                style={{
                  // Each page is exactly one viewport wide: (100 / pages)% of
                  // the row (which itself is `pages × 100%` of the viewport).
                  width: `${100 / pages}%`,
                  flexShrink: 0,
                  display: 'grid',
                  gridTemplateColumns: `repeat(${perPage}, minmax(0, 1fr))`,
                  gap: compact ? 6 : 12,
                  padding: '2px 4px',
                  boxSizing: 'border-box',
                }}
              >
                {slots.map((a, i) => a ? (
                  <AmbassadorCard
                    key={`${pIdx}-${a.slug}-${i}`}
                    amb={a}
                    cardBg={cardBg}
                    accent={accent}
                    s={s}
                    p={p}
                    compact={compact}
                    onDonate={onCardClick ? () => onCardClick(a.slug) : undefined}
                    previewDonate={previewDonate}
                    active={activeSlug === a.slug}
                  />
                ) : (
                  <div key={`${pIdx}-empty-${i}`} />
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {showControls && (
        <>
          {/* RTL-convention arrows: in Hebrew flow "next" lives to the LEFT.
              dir="ltr" prevents Unicode bidi from mirroring the chevron
              glyphs (without it, ‹ renders visually as › and vice versa
              inside the dir=rtl scroll container). */}
          {/* LTR-convention arrow mapping: chevron `‹` (left side) goes to
              the previous page, `›` (right side) advances to the next. Many
              donors have learned this from English UIs even on Hebrew sites,
              so matching the visual direction of the chevron with the
              direction of travel keeps the click intent obvious. */}
          <button
            type="button"
            aria-label="הקודם"
            dir="ltr"
            onClick={() => goTo(page - 1)}
            style={{ ...arrowStyle, left: 0 }}
          >‹</button>
          <button
            type="button"
            aria-label="הבא"
            dir="ltr"
            onClick={() => goTo(page + 1)}
            style={{ ...arrowStyle, right: 0 }}
          >›</button>

          <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 12 }}>
            {Array.from({ length: pages }, (_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`עבור לעמוד ${i + 1}`}
                style={{
                  width: i === page ? 22 : 8, height: 8, borderRadius: 999,
                  background: i === page ? accent : '#d1d5db',
                  border: 'none', cursor: 'pointer', padding: 0,
                  transition: 'width 0.3s, background 0.3s',
                }}
              />
            ))}
          </div>
        </>
      )}

      {showAllButton && list.length > perPage && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          style={{
            display: 'block',
            margin: '12px auto 0',
            padding: '8px 20px',
            borderRadius: 999,
            border: `2px solid ${accent}`,
            background: 'transparent',
            color: accent,
            fontWeight: 700,
            fontSize: 13,
            cursor: 'pointer',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = `${accent}11`; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          {t('public.ambassadors.showAll')} ({list.length})
        </button>
      )}

      {showAll && (
        <AmbassadorAllModal
          list={list}
          cardBg={cardBg}
          accent={accent}
          s={s}
          p={p}
          title={titleEff}
          onClose={() => setShowAll(false)}
          campaignSlug={campaignSlug}
          onCardClick={onCardClick}
        />
      )}
    </div>
  );
};

// List layout: 6 visible stacked, "הצג עוד" reveals the rest in a modal
// (with per-ambassador donor drill-down).
const AmbassadorList: React.FC<{
  list: AmbassadorItem[]; cardBg: string; accent: string; s: any; p: any;
  title: string; onCardClick?: (slug: string) => void; activeSlug?: string | null;
  campaignSlug?: string; previewDonate?: boolean;
}> = ({ list, cardBg, accent, s, p, title, onCardClick, activeSlug, campaignSlug, previewDonate }) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const VISIBLE = 6;
  const visible = list.slice(0, VISIBLE);
  const remaining = list.length - VISIBLE;

  if (list.length === 0) {
    return (
      <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: 20 }}>
        {t('public.ambassadors.noAmbassadorsYet')}
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {visible.map((amb, i) => (
          <AmbassadorCard
            key={`${amb.slug}-${i}`}
            amb={amb}
            cardBg={cardBg}
            accent={accent}
            s={s}
            p={p}
            onDonate={onCardClick ? () => onCardClick(amb.slug) : undefined}
            previewDonate={previewDonate}
            active={activeSlug === amb.slug}
          />
        ))}
      </div>
      {remaining > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          style={{
            alignSelf: 'center',
            marginTop: 12,
            padding: '10px 22px',
            borderRadius: 999,
            border: `2px solid ${accent}`,
            background: 'transparent',
            color: accent,
            fontWeight: 700,
            fontSize: 13,
            cursor: 'pointer',
            transition: 'background 0.2s, transform 0.1s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = `${accent}11`; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          {t('public.ambassadors.showMore')} ({remaining})
        </button>
      )}
      {showAll && (
        <AmbassadorAllModal
          list={list}
          cardBg={cardBg}
          accent={accent}
          s={s}
          p={p}
          title={title}
          onClose={() => setShowAll(false)}
          campaignSlug={campaignSlug}
          onCardClick={onCardClick}
        />
      )}
    </>
  );
};

export function sortAmbassadors(list: AmbassadorItem[], sortBy: string | undefined): AmbassadorItem[] {
  if (!sortBy || sortBy === 'manual') return list;
  const copy = [...list];
  switch (sortBy) {
    case 'raised-desc':  return copy.sort((a, b) => b.raised - a.raised);
    case 'raised-asc':   return copy.sort((a, b) => a.raised - b.raised);
    case 'name-asc':     return copy.sort((a, b) => a.name.localeCompare(b.name, 'he'));
    case 'name-desc':    return copy.sort((a, b) => b.name.localeCompare(a.name, 'he'));
    case 'donors-desc':  return copy.sort((a, b) => b.donorsCount - a.donorsCount);
    case 'goal-desc':    return copy.sort((a, b) => b.goal - a.goal);
    case 'progress-desc':return copy.sort((a, b) => {
      const pa = a.goal > 0 ? a.raised / a.goal : 0;
      const pb = b.goal > 0 ? b.raised / b.goal : 0;
      return pb - pa;
    });
    default:             return list;
  }
}

// Top-level block: handles layout dispatch (carousel vs list), sort, accent,
// forced raised/goal/progress on the headline metrics. Outer block wrapper
// (id ref, position, dynamic-height measurement) is left to the parent.
// "הצטרף כשגריר" — public self-registration. Shown at the bottom of the
// ambassadors block when the owner enabled `allowSelfJoin` in the builder.
// Posts to the public join endpoint and hands back a personal share link.
const AmbassadorJoinButton: React.FC<{ campaignSlug?: string; accent: string; isPreview?: boolean }> = ({ campaignSlug, accent, isPreview }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', goal: '' });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ shareUrl: string; name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    if (!form.name.trim()) { setError('נא להזין שם'); return; }
    if (isPreview || !campaignSlug) { setError('הצטרפות זמינה בדף החי של הקמפיין'); return; }
    setSubmitting(true); setError(null);
    try {
      const res = await axios.post(`${API_BASE}/public/campaigns/${campaignSlug}/ambassadors/join`, {
        name: form.name.trim(), phone: form.phone.trim(), email: form.email.trim(),
        goal: form.goal ? Number(form.goal) : undefined,
      });
      const shareUrl = res.data?.shareUrl
        || `${window.location.origin}${window.location.pathname}?ref=${res.data?.slug}`;
      setResult({ shareUrl, name: res.data?.name || form.name.trim() });
    } catch (e: any) {
      setError(e?.response?.data?.error || 'ההצטרפות נכשלה, נסו שוב');
    } finally { setSubmitting(false); }
  };

  const copy = () => {
    if (result) { navigator.clipboard?.writeText(result.shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); }
  };

  const inputStyle: React.CSSProperties = { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, marginBottom: 8, boxSizing: 'border-box' };

  return (
    <>
      <button
        type="button"
        onClick={() => { setOpen(true); setResult(null); setError(null); }}
        style={{ alignSelf: 'center', padding: '10px 22px', borderRadius: 999, border: 'none', background: accent, color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}
      >
        ➕ הצטרף כשגריר
      </button>
      {open && createPortal(
        <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
          <div onClick={e => e.stopPropagation()} dir="rtl" style={{ background: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 420, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
            {result ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 40 }}>🎉</div>
                <h3 style={{ margin: '8px 0', fontSize: 18, fontWeight: 800 }}>הצטרפת בהצלחה, {result.name}!</h3>
                <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>שתפו את הלינק האישי שלכם כדי לגייס תרומות:</p>
                <div style={{ ...inputStyle, direction: 'ltr', wordBreak: 'break-all', background: '#f9fafb', textAlign: 'left', marginBottom: 8 }}>{result.shareUrl}</div>
                <button type="button" onClick={copy} style={{ width: '100%', padding: 10, borderRadius: 8, border: 'none', background: accent, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>{copied ? '✓ הועתק' : 'העתק לינק'}</button>
              </div>
            ) : (
              <>
                <h3 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 800 }}>הצטרפות כשגריר</h3>
                <input style={inputStyle} placeholder="שם מלא *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                <input style={inputStyle} placeholder="טלפון" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                <input style={inputStyle} placeholder="אימייל" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                <input style={inputStyle} type="number" placeholder="יעד גיוס (₪, אופציונלי)" value={form.goal} onChange={e => setForm({ ...form, goal: e.target.value })} />
                {error && <p style={{ color: '#dc2626', fontSize: 12, margin: '4px 0' }}>{error}</p>}
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button type="button" onClick={() => setOpen(false)} style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' }}>ביטול</button>
                  <button type="button" onClick={submit} disabled={submitting} style={{ flex: 2, padding: 10, borderRadius: 8, border: 'none', background: accent, color: '#fff', fontWeight: 700, cursor: 'pointer', opacity: submitting ? 0.6 : 1 }}>{submitting ? 'שולח…' : 'הצטרף'}</button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export const AmbassadorsPublicBlock: React.FC<{
  list: AmbassadorItem[];
  s: any;
  p: any;
  primary?: string;
  isMobile: boolean;
  onCardClick?: (slug: string) => void;
  activeSlug?: string | null;
  // When set, "הצג הכל" → modal drill-down fetches donors per ambassador
  // from /public/campaigns/:slug/ambassadors-public/:ambSlug/donors. Omitted
  // in the builder preview (no real campaign slug), so the drill-down is
  // still navigable but shows the loading/empty state for donors.
  campaignSlug?: string;
  // Render the "donate via" button even without a click handler (editor preview)
  // so the designer sees the real card height and can space the block correctly.
  previewDonate?: boolean;
}> = ({ list: rawList, s, p, primary, isMobile, onCardClick, activeSlug, campaignSlug, previewDonate }) => {
  const { t } = useTranslation();
  const list = sortAmbassadors(rawList, p.sortBy);
  const accent = p.accentColor || primary || '#2563eb';
  const cardBg = p.cardBgColor || '#ffffff';
  const layoutMode = p.layout || 'grid';
  // Default title — 'השגרירים שלנו' in HE / 'Our Ambassadors' in EN.
  const titleText = p.title || t('public.ambassadors.title');
  // mobile carousel: 1 card/page + "הצג הכל" button → modal
  // desktop carousel: 3 cards/page with arrows + dots
  const perPage = isMobile ? 1 : 3;
  // Force the headline metrics on every card. Block-level toggles still
  // control the donor strip / donor count.
  const cardP = { ...p, showRaised: true, showGoal: true, showProgress: true };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} dir={getBootLocale() === 'en' ? 'ltr' : 'rtl'}>
      {p.title && (
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: s.textColor || '#111827', textAlign: 'center' }}>
          {p.title}
        </h3>
      )}
      {layoutMode === 'list' ? (
        <AmbassadorList
          list={list}
          cardBg={cardBg}
          accent={accent}
          s={s}
          p={cardP}
          title={titleText}
          onCardClick={onCardClick}
          activeSlug={activeSlug}
          campaignSlug={campaignSlug}
          previewDonate={previewDonate}
        />
      ) : (
        <AmbassadorCarousel
          list={list}
          perPage={perPage}
          cardBg={cardBg}
          accent={accent}
          s={s}
          p={cardP}
          onCardClick={onCardClick}
          activeSlug={activeSlug}
          // Show the "הצג הכל" button on every viewport — even on desktop
          // where the carousel arrows exist, donors often prefer scanning
          // the full roster at once. The button hides itself automatically
          // when there's nothing to "show more" (list.length ≤ perPage).
          showAllButton
          title={titleText}
          campaignSlug={campaignSlug}
          previewDonate={previewDonate}
        />
      )}
      {p.allowSelfJoin && (
        <AmbassadorJoinButton campaignSlug={campaignSlug} accent={accent} isPreview={!!previewDonate} />
      )}
    </div>
  );
};
